import { GameRuntime } from "@/game/runtime";
import { StreamChatMessage } from "@/shared/types/chat";
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_MESSAGES = 6;
const CHAT_EXIT_MS = 220;

export const useStreamChatFeed = (runtime: GameRuntime) => {
  const [messages, setMessages] = useState<StreamChatMessage[]>([]);
  const timeoutMapRef = useRef(new Map<string, number>());
  const [exitingIds, setExitingIds] = useState<string[]>([]);

  const clearMessageTimer = useCallback((id: string) => {
    const timeoutId = timeoutMapRef.current.get(id);
    if (timeoutId == null) return;

    window.clearTimeout(timeoutId);
    timeoutMapRef.current.delete(id);
  }, []);

  const removeMessage = useCallback(
    (id: string) => {
      clearMessageTimer(id);
      setExitingIds((current) =>
        current.includes(id) ? current : [...current, id],
      );

      window.setTimeout(() => {
        setMessages((current) =>
          current.filter((message) => message.id !== id),
        );
        setExitingIds((current) => current.filter((value) => value !== id));
      }, CHAT_EXIT_MS);
    },
    [clearMessageTimer],
  );

  const pushMessage = useCallback(
    (message: StreamChatMessage) => {
      const timeoutId = window.setTimeout(() => {
        runtime.expireChatMessage(message);
        removeMessage(message.id);
      }, message.ttlMs);

      timeoutMapRef.current.set(message.id, timeoutId);

      setMessages((current) => {
        const next = [...current, message];
        const overflow = next.length - MAX_MESSAGES;

        if (overflow <= 0) {
          return next;
        }

        for (const trimmed of next.slice(0, overflow)) {
          clearMessageTimer(trimmed.id);
          runtime.expireChatMessage(trimmed);
        }

        setExitingIds((current) =>
          current.filter((id) =>
            next.slice(overflow).some((item) => item.id === id),
          ),
        );

        return next.slice(-MAX_MESSAGES);
      });
    },
    [clearMessageTimer, removeMessage, runtime],
  );

  useEffect(() => {
    const unsubscribe = runtime.on("chatMessageSpawned", ({ message }) => {
      pushMessage(message);
    });

    return () => {
      unsubscribe?.();

      for (const timeoutId of timeoutMapRef.current.values()) {
        window.clearTimeout(timeoutId);
      }

      timeoutMapRef.current.clear();
    };
  }, [pushMessage, runtime]);

  const moderateMessage = useCallback(
    (message: StreamChatMessage) => {
      runtime.moderateChatMessage(message);
      removeMessage(message.id);
    },
    [removeMessage, runtime],
  );

  return {
    messages,
    exitingIds,
    moderateMessage,
  };
};
